import { createRoot, hydrateRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { ApolloProvider, type NormalizedCacheObject } from "@apollo/client";
import { BrowserRouter } from "react-router-dom";
import { TooltipProvider } from "@appcaire/ui/components/ui";
import App from "./App";
import "@appcaire/ui/styles/index.css";
import "@appcaire/ui/themes/nacka.css";
import "./index.css";
import { apolloClient, restoreApolloState } from "./lib/apollo";
import { initGA } from "./lib/analytics";

declare global {
  interface Window {
    __APOLLO_STATE__?: NormalizedCacheObject;
  }
}

// Initialize Google Analytics
initGA();

/**
 * Client-side entry point for hydration
 * This is used in SSR mode to hydrate the server-rendered HTML
 */
const root = document.getElementById("root");

if (!root) {
  throw new Error("Root element not found");
}

// Start hydration from the same route data that produced the server markup.
restoreApolloState(window.__APOLLO_STATE__);
delete window.__APOLLO_STATE__;

const app = (
  <HelmetProvider>
    <ApolloProvider client={apolloClient as never}>
      <TooltipProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </TooltipProvider>
    </ApolloProvider>
  </HelmetProvider>
);

const hasServerMarkup = root.querySelector("*") !== null;

if (hasServerMarkup) {
  hydrateRoot(root, app, {
    onRecoverableError: (error) => {
      if (import.meta.env.DEV) {
        console.warn("Hydration error:", error);
      }
    },
  });
} else {
  createRoot(root).render(app);
}
